import { useState } from 'react'
import { useNavigate } from 'react-router'

import { isApproverOrHigherRole, isVesselAdminOrHigherRole } from '@ankor-io/common/domain/iam/account'
import { AwxConnectedAccountType } from '@ankor-io/common/types/awx'

import { useModuleAvailabilityQuery } from '@/api/queries/useVesselQuery'
import { useConnectedAccount } from '@/providers/connectedAccount'
import { useUserContext } from '@/providers/user/useUserContext'
import { ActivateModuleModal } from '@/views/components/ActivateModuleModal'
import { AccountActivationPrompt } from '@/views/components/Dashboard/AccountActivationPrompt'

import DashboardCharters from './DashboardCharters'
import DashboardTransactions from './DashboardTransactions'

export const ConnectedAccountDetail = () => {
  const navigate = useNavigate()
  const { connectedAccounts, refreshConnectedAccounts } = useConnectedAccount()
  const { activeConnectedAccountId, activeOrganizationRole, activeVesselId } = useUserContext()

  const [isRefreshing, setIsRefreshing] = useState(false)
  const [showActivateModal, setShowActivateModal] = useState(false)

  const { data: moduleAvailability } = useModuleAvailabilityQuery({ vesselId: activeVesselId })

  const selectedConnectedAccount = connectedAccounts.find((v) => v.id === activeConnectedAccountId)
  const moduleInfo = moduleAvailability?.modules.find((m) => m.type === AwxConnectedAccountType.APA) ?? null

  const handleProceedToKYC = async () => {
    setIsRefreshing(true)
    try {
      // Refresh connected account status before navigation
      await refreshConnectedAccounts()
    } catch (error) {
      console.error('Error refreshing connected accounts:', error)
    }

    // Navigate away — no need to clear loading state (component unmounts)
    navigate('/kyc-rfi', { replace: true })
  }

  return (
    <div className="space-y-6">
      {selectedConnectedAccount && (
        <AccountActivationPrompt
          connectedAccount={selectedConnectedAccount}
          moduleAvailability={moduleInfo}
          onContinue={handleProceedToKYC}
          onActivateModule={() => setShowActivateModal(true)}
          isLoading={isRefreshing}
        />
      )}

      {/* Charters overview — management surface, hidden from approvers and below */}
      {isVesselAdminOrHigherRole(activeOrganizationRole) && <DashboardCharters />}

      {/* Transactions require approver or higher role */}
      {isApproverOrHigherRole(activeOrganizationRole) && <DashboardTransactions />}

      {showActivateModal && (
        <ActivateModuleModal
          isOpen={true}
          onClose={() => {
            setShowActivateModal(false)
            refreshConnectedAccounts()
          }}
          moduleType={AwxConnectedAccountType.APA}
        />
      )}
    </div>
  )
}

export default ConnectedAccountDetail
